/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * Unified player state and event enum.
 *
 * Lifecycle states follow a deterministic state machine:
 *
 * IDLE → LOADING → READY → PLAYING ⇄ PAUSED
 *                    ↕        ↕         ↕
 *               BUFFERING  SEEKING   ENDED
 *                    ↕        ↕
 *                  ERROR   DESTROYED
 *
 * Additional values represent player events emitted via on()/off().
 * These are not persistent states but transient notifications.
 */
export enum PlayerState {
  // ── Lifecycle States ─────────────────────────────────────────────────────

  /** Player is initialized but no source is loaded. */
  IDLE = "IDLE",
  /** Source is being loaded (manifest fetch + parse in progress). */
  LOADING = "LOADING",
  /** Source loaded and manifest parsed; ready to begin playback. */
  READY = "READY",
  /** Media is actively playing. */
  PLAYING = "PLAYING",
  /** Playback is paused by user or application. */
  PAUSED = "PAUSED",
  /** Player is rebuffering (stalled waiting for data). */
  BUFFERING = "BUFFERING",
  /** Player is performing a seek operation. */
  SEEKING = "SEEKING",
  /** Playback reached end of stream. */
  ENDED = "ENDED",
  /** Player encountered a critical error. */
  ERROR = "ERROR",
  /** Player instance has been destroyed and cannot be reused. */
  DESTROYED = "DESTROYED",

  // ── Player Events ────────────────────────────────────────────────────────

  /** ABR status changed (enabled/disabled). */
  ABR_STATUS_CHANGED = "ABR_STATUS_CHANGED",
  /** Adaptive bitrate switch occurred. */
  ADAPTATION = "ADAPTATION",
  /** Playback reached 100% of VOD content. */
  COMPLETE = "COMPLETE",
  /** DRM configuration applied. */
  DRM_CONFIGURED = "DRM_CONFIGURED",
  /** DRM configuration or license acquisition failed. */
  DRM_FAILED = "DRM_FAILED",
  /** DRM session updated (license response processed). */
  DRM_SESSION_UPDATE = "DRM_SESSION_UPDATE",
  /** Segment download failed. */
  DOWNLOAD_FAILED = "DOWNLOAD_FAILED",
  /** HTTP response headers received for a segment download. */
  DOWNLOAD_HEADERS_RECEIVED = "DOWNLOAD_HEADERS_RECEIVED",
  /** EMSG (Event Message Box) encountered in the stream. */
  EMSG = "EMSG",
  /** Media timeline fully consumed. */
  END_OF_STREAM = "END_OF_STREAM",
  /** Player entered automatic error-recovery / retry. */
  ERROR_RECOVERY = "ERROR_RECOVERY",
  /** EME session expiration time updated. */
  EXPIRATION_UPDATED = "EXPIRATION_UPDATED",
  /** Unrecoverable fatal error terminated playback. */
  FATAL_ERROR = "FATAL_ERROR",
  /** First video frame rendered. */
  FIRST_FRAME = "FIRST_FRAME",
  /** GapJumpingController jumped a gap in the media timeline. */
  GAP_JUMPED = "GAP_JUMPED",
  /** Player begins its initial load sequence. */
  INITIATION = "INITIATION",
  /** EME key status changed (usable, expired, output-restricted). */
  KEY_STATUS_CHANGED = "KEY_STATUS_CHANGED",
  /** Source fully loaded and ready to play. */
  LOADED = "LOADED",
  /** Manifest parsed. */
  MANIFEST_PARSED = "MANIFEST_PARSED",
  /** Live manifest updated (new segments available). */
  MANIFEST_UPDATED = "MANIFEST_UPDATED",
  /** Media quality of the active stream changed. */
  MEDIA_QUALITY_CHANGED = "MEDIA_QUALITY_CHANGED",
  /** Player has no active source or session. */
  NONE = "NONE",
  /** Playback paused (HTML5 pause). */
  PAUSE = "PAUSE",
  /** Player paused beyond configured threshold. */
  PAUSED_THRESHOLD = "PAUSED_THRESHOLD",
  /** Producer Reference Time (PRFT) box encountered. */
  PRFT = "PRFT",
  /** Playback rate changed (trick play). */
  RATE_CHANGE = "RATE_CHANGE",
  /** Seek operation completed. */
  SEEKED = "SEEKED",
  /** Media segment appended to the SourceBuffer. */
  SEGMENT_APPENDED = "SEGMENT_APPENDED",
  /** HLS session data (EXT-X-SESSION-DATA) encountered. */
  SESSION_DATA = "SESSION_DATA",
  /** Spatial video information present in the stream. */
  SPATIAL_VIDEO_INFO = "SPATIAL_VIDEO_INFO",
  /** Player stalled due to network or decode issues. */
  STALLED = "STALLED",
  /** StallDetector detected a playback stall. */
  STALL_DETECTED = "STALL_DETECTED",
  /** Playback started for the first time after load. */
  STARTED = "STARTED",
  /** Player state machine transitioned. */
  STATE_CHANGED = "STATE_CHANGED",
  /** Streaming engine began streaming content. */
  STREAMING = "STREAMING",
  /** Active text track changed. */
  TEXT_CHANGED = "TEXT_CHANGED",
  /** Periodic time update with current position. */
  TIME_UPDATE = "TIME_UPDATE",
  /** New timeline region added to the manifest. */
  TIMELINE_REGION_ADDED = "TIMELINE_REGION_ADDED",
  /** Playhead entered a timeline region. */
  TIMELINE_REGION_ENTER = "TIMELINE_REGION_ENTER",
  /** Playhead exited a timeline region. */
  TIMELINE_REGION_EXIT = "TIMELINE_REGION_EXIT",
  /** Available tracks (audio/video/text) changed. */
  TRACKS_CHANGED = "TRACKS_CHANGED",
  /** Player fully unloaded the current source. */
  UNLOADED = "UNLOADED",
  /** Player began unloading the current source. */
  UNLOADING = "UNLOADING",
  /** Active variant track changed. */
  VARIANT_CHANGED = "VARIANT_CHANGED"
}

/**
 * Maps PlayerState values to Shaka Player / HTMLVideoElement event names.
 * Only entries that correspond to actual Shaka or HTML5 events are included.
 * Custom events (INITIATION, FIRST_FRAME, etc.) are not in this map
 * because they are emitted application-side, not by Shaka.
 */
export const SHAKA_EVENT_NAME: Partial<Record<PlayerState, string>> = {
  [PlayerState.ABR_STATUS_CHANGED]: "abrstatuschanged",
  [PlayerState.ADAPTATION]: "adaptation",
  [PlayerState.BUFFERING]: "buffering",
  [PlayerState.COMPLETE]: "complete",
  [PlayerState.DOWNLOAD_FAILED]: "downloadfailed",
  [PlayerState.DOWNLOAD_HEADERS_RECEIVED]: "downloadheadersreceived",
  [PlayerState.DRM_SESSION_UPDATE]: "drmsessionupdate",
  [PlayerState.EMSG]: "emsg",
  [PlayerState.ENDED]: "ended",
  [PlayerState.ERROR]: "error",
  [PlayerState.EXPIRATION_UPDATED]: "expirationupdated",
  [PlayerState.GAP_JUMPED]: "gapjumped",
  [PlayerState.KEY_STATUS_CHANGED]: "keystatuschanged",
  [PlayerState.LOADED]: "loaded",
  [PlayerState.LOADING]: "loading",
  [PlayerState.MANIFEST_PARSED]: "manifestparsed",
  [PlayerState.MANIFEST_UPDATED]: "manifestupdated",
  [PlayerState.MEDIA_QUALITY_CHANGED]: "mediaqualitychanged",
  [PlayerState.PAUSE]: "pause",
  [PlayerState.PLAYING]: "playing",
  [PlayerState.PRFT]: "prft",
  [PlayerState.RATE_CHANGE]: "ratechange",
  [PlayerState.SEEKED]: "seeked",
  [PlayerState.SEEKING]: "seeking",
  [PlayerState.SEGMENT_APPENDED]: "segmentappended",
  [PlayerState.SESSION_DATA]: "sessiondata",
  [PlayerState.SPATIAL_VIDEO_INFO]: "spatialvideoinfo",
  [PlayerState.STALLED]: "stalled",
  [PlayerState.STALL_DETECTED]: "stalldetected",
  [PlayerState.STARTED]: "started",
  [PlayerState.STREAMING]: "streaming",
  [PlayerState.TEXT_CHANGED]: "textchanged",
  [PlayerState.TIME_UPDATE]: "timeupdate",
  [PlayerState.TIMELINE_REGION_ADDED]: "timelineregionadded",
  [PlayerState.TIMELINE_REGION_ENTER]: "timelineregionenter",
  [PlayerState.TIMELINE_REGION_EXIT]: "timelineregionexit",
  [PlayerState.TRACKS_CHANGED]: "trackschanged",
  [PlayerState.UNLOADING]: "unloading",
  [PlayerState.VARIANT_CHANGED]: "variantchanged"
};
